import { NgTemplateOutlet, UpperCasePipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core'
import { RouterLink } from '@angular/router'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions, ofType } from '@ngrx/effects'
import { filter } from 'rxjs'

import { Account } from '@account/models'
import type { Bid, BidUi } from '@app/models/bid'
import type { ProfileFull } from '@app/models/profile'
import type { ConversationProfile } from '@app/modules/messaging/models/conversation-profile.model'
import * as fromTopAdmirers from '@app/modules/top-admirers/store'
import { ModalService } from '@app/overlays/modal/modal.service'
import { ObfuscateNamePipe } from '@ui/pipes/obfuscate-name.pipe'

import { ButtonComponent } from '../../../ui/components/button/button.component'
import { IconComponent } from '../../../ui/components/icon/icon.component'
import { BgPipe } from '../../../ui/pipes/bg/bg.pipe'
import type { BidSource } from '../bid/bid.component'
import { BidListComponent } from '../bid-list/bid-list.component'
import { TopAdmirerBadgeComponent } from '../top-admirer-badge/top-admirer-badge.component'
import { SiteStore } from '@app/store/site.store'
import { Closeable } from '@app/overlays/closeable'

@UntilDestroy()
@Component({
  selector: 'sb-top-admirers',
  templateUrl: './top-admirers.component.html',
  styleUrls: ['./top-admirers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    TopAdmirerBadgeComponent,
    RouterLink,
    NgTemplateOutlet,
    IconComponent,
    UpperCasePipe,
    BgPipe,
    ObfuscateNamePipe,
  ],
})
export class TopAdmirersComponent extends Closeable {
  readonly account = inject(Account)
  private readonly siteStore = inject(SiteStore)
  private readonly modalService = inject(ModalService)
  private readonly actions$ = inject(Actions)

  readonly DISTINGUISHED_BIDS_COUNT = 3
  readonly MAX_VISIBLE_BIDS_COUNT = 10

  private readonly _bids = signal<Bid[]>([])
  private readonly showMore = signal(false)

  /* eslint-disable @angular-eslint/prefer-signals -- created imperatively via ModalService */
  @Input({ required: true }) me!: boolean
  @Input({ required: true }) source!: BidSource
  @Input({ required: true }) profile!: ProfileFull | ConversationProfile
  @Input() set bids(bids: Bid[]) {
    this._bids.set(bids ?? [])
  }
  /* eslint-enable @angular-eslint/prefer-signals */

  get bids(): Bid[] {
    return this._bids()
  }

  @Output() placeBid = new EventEmitter<void>()

  private readonly myBid = computed(() => this._bids().find(bid => bid.benefactor_id === this.account.id))

  readonly isTopAdmirer = computed(() => {
    const myBid = this.myBid()
    return Boolean(myBid && this._bids()[0] === myBid)
  })

  readonly isMyBidCapped = computed(() => this.myBid()?.amount === this.siteStore.SA.Constants.max_bid)

  readonly displayBids = computed<BidUi[]>(() => {
    const bids = this._bids()
    return this.showMore()
      ? bids.slice(0, this.MAX_VISIBLE_BIDS_COUNT).map((bid, index) => ({ ...bid, rankingPlace: index + 1 }))
      : this.prepareBids(bids)
  })

  constructor() {
    super()
    this.observeBidSuccess()
  }

  viewMore(): void {
    this.showMore.set(true)
  }

  viewAll(): void {
    const { instance } = this.modalService.create(BidListComponent, {
      closeActionStyle: 'button',
      desktopHeight: '580px',
    })
    instance.bids = this._bids()
    instance.distinguishedBidsCount = this.DISTINGUISHED_BIDS_COUNT
  }

  private prepareBids(bids: Bid[]): BidUi[] {
    const topBids: BidUi[] = bids
      .slice(0, this.DISTINGUISHED_BIDS_COUNT)
      .map((bid, index) => ({ ...bid, rankingPlace: index + 1 }))
    const myBid = this.myBid()
    if (myBid) {
      const myBidPlace = bids.indexOf(myBid)
      if (myBidPlace >= this.DISTINGUISHED_BIDS_COUNT) {
        topBids.push({ ...myBid, rankingPlace: myBidPlace + 1 })
      }
    }
    return topBids
  }

  private observeBidSuccess(): void {
    this.actions$
      .pipe(
        ofType(fromTopAdmirers.Actions.bidSuccess),
        filter(({ profileId }) => profileId === this.profile.id),
        untilDestroyed(this),
      )
      .subscribe(({ bids }) => this._bids.set(bids))
  }
}
